/**
 * Shared S2S gRPC client construction + cold-channel readiness helpers.
 *
 * WHY THIS EXISTS (TIER-3B cold-channel first-call resilience):
 *   The FIRST RPC on a freshly-created (cold) gRPC client channel can
 *   transiently fail — under Bun especially — with
 *   `13 INTERNAL: lookup failed`. This is the grpc-js/Bun cold-channel
 *   trap: at construction the channel is in state IDLE and has done NO name
 *   resolution or TCP connect yet. The first call races the resolver's
 *   first DNS lookup + connect; if the call reaches the transport before the
 *   subchannel is READY the resolver can surface a transient `lookup failed`
 *   that a RETRY (once warm) does not reproduce. In a freshly-DEPLOYED
 *   service this 500s the very first cross-service request after boot.
 *
 *   grpc-js gives us a clean primitive to avoid the race: `waitForReady`,
 *   which blocks until the channel connects (or the deadline elapses),
 *   forcing name resolution + the initial connect BEFORE the first RPC.
 *   But services build their S2S clients ad-hoc —
 *   `new XClient(target, credentials.createInsecure(), { interceptors: […] })`
 *   — with no shared factory and no `waitForReady`, so every service
 *   re-implements (or, today, omits) the warm-up. This module gives them a
 *   single, composable place to (a) await readiness on an existing client
 *   and (b) build an auth-wired S2S client that optionally warms itself.
 *
 * NON-BREAKING: this is an ADDITION. `createClientAuthInterceptor` and all
 * existing `new XClient(...)` call-sites keep working untouched. Adopting
 * `waitForClientReady` / `buildS2SClient` is opt-in.
 */
import {
  type ChannelCredentials,
  type Client,
  type Interceptor,
  credentials as grpcCredentials,
} from "@grpc/grpc-js";
import {
  type ClientAuthOptions,
  createClientAuthInterceptor,
} from "./auth-client-interceptor";

/**
 * The structural subset of a `@grpc/grpc-js` `Client` this module needs.
 * Every generated `*Client` (which extends `grpc.Client`) is assignable to
 * it, so callers never have to widen a concrete client type.
 */
export interface GrpcClientLike {
  waitForReady(
    deadline: number | Date,
    callback: (error?: Error) => void,
  ): void;
}

/**
 * A generated-client constructor shape:
 * `new (address, credentials, options?) => T`. Every protoc/ts-proto grpc
 * client class matches this.
 */
export type GrpcClientCtor<T extends Client> = new (
  address: string,
  channelCredentials: ChannelCredentials,
  options?: {
    interceptors?: Interceptor[];
    [key: string]: unknown;
  },
) => T;

/** Default readiness deadline: enough to cover a cold DNS lookup + connect. */
export const DEFAULT_WAIT_FOR_READY_MS = 5_000;

/**
 * Promise wrapper around `client.waitForReady(deadline, cb)`.
 *
 * Resolves once the channel reaches READY (name resolution + initial connect
 * done — so the NEXT RPC is warm and does not race the resolver). Rejects if
 * the channel is not ready within `deadlineMs`. Use this once, right after
 * constructing a client (typically at service boot), so a freshly-deployed
 * service's first peer call does not 500 on the grpc-js/Bun cold-channel
 * `13 INTERNAL: lookup failed` race.
 *
 * @param client    Any grpc-js client (or generated subclass).
 * @param deadlineMs Milliseconds from now to wait. Defaults to
 *                   {@link DEFAULT_WAIT_FOR_READY_MS}.
 */
export function waitForClientReady(
  client: GrpcClientLike,
  deadlineMs: number = DEFAULT_WAIT_FOR_READY_MS,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    // grpc-js accepts an absolute deadline (epoch ms or Date). Compute it
    // fresh here so callers pass a RELATIVE budget, not an absolute time.
    const deadline = Date.now() + deadlineMs;
    client.waitForReady(deadline, (error?: Error) => {
      if (error) {
        reject(error);
        return;
      }
      resolve();
    });
  });
}

/** Options for {@link buildS2SClient}. */
export interface BuildS2SClientOptions extends ClientAuthOptions {
  /**
   * Channel credentials. Defaults to `credentials.createInsecure()` — the
   * S2S plaintext-over-SG default (mTLS graduation is a separate concern;
   * pass explicit `createSsl(...)` creds to opt in). The auth interceptor
   * carries the bearer token regardless of channel security.
   */
  channelCredentials?: ChannelCredentials;
  /**
   * Extra client interceptors to compose AFTER the auth interceptor (auth
   * runs first so downstream interceptors see the `authorization` metadata).
   */
  extraInterceptors?: Interceptor[];
  /**
   * Extra channel options merged into the client options (e.g.
   * `"grpc.keepalive_time_ms"`). `interceptors` here is ignored — use
   * `extraInterceptors`.
   */
  channelOptions?: Record<string, unknown>;
  /**
   * When set (> 0), `buildS2SClient` is `async` and AWAITS
   * {@link waitForClientReady} with this deadline before returning, so the
   * returned client is already warm (cold-channel first-call resilience). A
   * readiness failure REJECTS the returned promise. When 0 / undefined the
   * client is returned synchronously WITHOUT warming (caller may warm later).
   */
  waitForReadyMs?: number;
}

/**
 * Build an S2S gRPC client wired with the `@nodii/grpc-auth` client auth
 * interceptor (bearer-token acquisition + `x-user-context` forwarding) on an
 * insecure-by-default channel, and — when `waitForReadyMs` is set — warm the
 * cold channel before returning so the first peer call does not race the
 * resolver.
 *
 * Two overloads:
 *   - WITHOUT `waitForReadyMs` (or `0`): returns the client SYNCHRONOUSLY.
 *   - WITH a positive `waitForReadyMs`: returns a `Promise<T>` that resolves
 *     once the channel is READY (or rejects on the readiness deadline).
 *
 * @example
 * ```ts
 * // Warmed at boot — first call is cold-channel-safe.
 * const client = await buildS2SClient(BillingClient, target, {
 *   tokenManager, audience: "nodii-billing", scopes: ["invoices:read"],
 *   waitForReadyMs: 5000,
 * });
 * ```
 */
export function buildS2SClient<T extends Client>(
  Ctor: GrpcClientCtor<T>,
  target: string,
  options: BuildS2SClientOptions & { waitForReadyMs: number },
): Promise<T>;
export function buildS2SClient<T extends Client>(
  Ctor: GrpcClientCtor<T>,
  target: string,
  options: BuildS2SClientOptions,
): T;
export function buildS2SClient<T extends Client>(
  Ctor: GrpcClientCtor<T>,
  target: string,
  options: BuildS2SClientOptions,
): T | Promise<T> {
  const {
    channelCredentials,
    extraInterceptors,
    channelOptions,
    waitForReadyMs,
    ...authOptions
  } = options;

  const authInterceptor = createClientAuthInterceptor(
    authOptions as ClientAuthOptions,
  );
  const interceptors: Interceptor[] = [
    authInterceptor,
    ...(extraInterceptors ?? []),
  ];

  const client = new Ctor(
    target,
    channelCredentials ?? grpcCredentials.createInsecure(),
    { ...(channelOptions ?? {}), interceptors },
  );

  if (waitForReadyMs && waitForReadyMs > 0) {
    // Warm the cold channel, then hand back the client. A readiness failure
    // rejects — the caller decides whether a peer that will not connect at
    // boot is fatal (usually it is; better to fail loudly at boot than 500
    // the first real request).
    return waitForClientReady(
      client as unknown as GrpcClientLike,
      waitForReadyMs,
    ).then(() => client);
  }

  return client;
}
